import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styles from './Scrim.module.css';
import roundNavStyles from '../components/ScrimRoundNavigation.module.css';
import { getScrimTracks, launchScrim, updateScrimTrackOffset } from '../utils/api';
import ScrimYoutubePlayers from '../components/ScrimYoutubePlayers';
import ScrimTimeline from '../components/ScrimTimeline';
import ScrimMapNavigation from '../components/ScrimMapNavigation';
import ScrimRoundNavigation from '../components/ScrimRoundNavigation';
import Notes from '../components/Notes';
import RestartScrimProcessing from '../components/RestartScrimProcessing';
import ScrimHotkeysLabel from '../components/ScrimHotkeysLabel';
import AudioVolume from '../components/AudioVolume';
import { useAuthState } from '../utils/auth';
import { YoutubeVod } from '../components/Notes';
import { Spinner } from 'react-bootstrap';
import AddScrimTracks from '../components/AddScrimTracks';
import ScrimSettings from '../components/ScrimSettings';

export interface Round {
  map_index: number;
  round_number: number;
  round_start: number;
  round_end: number;
  teamA_score: number;
  teamB_score: number;
  winner: string;
  globalStartTime?: Date; // UTC time when round starts
  globalEndTime?: Date;   // UTC time when round ends
}

interface Track {
  youtube_id: string;
  track_index: number;
  yt_title: string;
  yt_channel: string;
  yt_stream_start_utc: string | null;
  yt_stream_end_utc: string | null;
  yt_stream_sync_offset?: number;
  processing_status?: string;
  processing_error?: string;
  rounds?: Round[];
}

interface VideoInfo {
  id: string;
  startTime: Date;
  duration: number;
  title: string;
}

type VideoQuality = 'hd1080' | 'hd720';

function Scrim() {
  const { scrim_id } = useParams<{ scrim_id: string }>();
  const navigate = useNavigate();
  
  // Auth state for Notes component
  const { authState } = useAuthState();

  const [tracks, setTracks] = useState<Track[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [videoInfos, setVideoInfos] = useState<VideoInfo[]>([]);
  const [globalStartTime, setGlobalStartTime] = useState<Date | null>(null);
  const [globalEndTime, setGlobalEndTime] = useState<Date | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [playersReady, setPlayersReady] = useState(false);
  const [isTimelineDragging, setIsTimelineDragging] = useState(false);
  const [wasPlayingBeforeDrag, setWasPlayingBeforeDrag] = useState(false);
  const [isSeekingToTarget, setIsSeekingToTarget] = useState(false);
  const [seekTargetTime, setSeekTargetTime] = useState(0);
  const seekTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isTimelineDraggingRef = useRef<boolean>(false);
  const isSeekingToTargetRef = useRef<boolean>(false);
  const lastSeekAtRef = useRef<number>(0);
  const initialRoundStartSecondsRef = useRef<number | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'single'>('single');
  const [activeVideoIndex, setActiveVideoIndex] = useState(0);
  const [selectedMap, setSelectedMap] = useState<number | null>(null); // null = "Full video"
  const [selectedRound, setSelectedRound] = useState<number | null>(null);
  const [videoQuality, setVideoQuality] = useState<VideoQuality>(() => {
    const stored = localStorage.getItem('scrim_video_quality');
    return stored === 'hd720' || stored === 'hd1080' ? (stored as VideoQuality) : 'hd720';
  });
  const [volume, setVolume] = useState(50); // Volume state for audio control
  const [prevVolume, setPrevVolume] = useState(50); // Previous volume before muting
  const [isQualityMenuOpen, setIsQualityMenuOpen] = useState(false);
  const qualityMenuRef = useRef<HTMLDivElement>(null);

  // Whether we have any processed rounds available
  const [hasProcessedRounds, setHasProcessedRounds] = useState<boolean>(false);
  // Ensure we only apply the initial default (map 1/round 1) once per session
  const didApplyInitialDefaultRef = useRef<boolean>(false);
  // Gate rendering until initial tracks + rounds + status are loaded and initial seek (if any) is applied
  const [isInitialReady, setIsInitialReady] = useState<boolean>(false);
  // Remember whether rounds were already available on the very first load
  const hadRoundsAtFirstLoadRef = useRef<boolean | null>(null);
  // Track live streams state
  const [hasLiveStreams, setHasLiveStreams] = useState<boolean>(false);
  const liveUpdateIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // State to trigger timeline end time recalculation for live streams
  const [liveTimeUpdate, setLiveTimeUpdate] = useState<number>(0);
  
  // Restart processing state
  const [showRestartModal, setShowRestartModal] = useState(false);
  const [isRestartingProcessing, setIsRestartingProcessing] = useState(false);
  
  // Add tracks modal state
  const [showAddTracksModal, setShowAddTracksModal] = useState(false);

  // Settings modal state
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  // Safety buffer in seconds to prevent seeking too close to live edge
  const [liveEdgeSafetyBuffer, setLiveEdgeSafetyBuffer] = useState(10); // seconds

  // Compute adjusted global end time for timeline when in full video mode with live streams
  // Limit to current UTC - liveEdgeSafetyBuffer to prevent seeking too close to live edge
  const timelineGlobalEndTime = useMemo(() => {
    // Only adjust when in full video mode (selectedMap === null) and we have live streams
    if (selectedMap !== null || !hasLiveStreams || !globalStartTime || !globalEndTime) {
      return globalEndTime;
    }
    
    // Calculate current UTC time minus liveEdgeSafetyBuffer
    // liveTimeUpdate is used as a dependency to trigger recalculation every second
    const now = new Date();
    const adjustedEndTime = new Date(now.getTime() - liveEdgeSafetyBuffer * 1000);
    
    // Ensure the adjusted time is not before the global start time
    if (adjustedEndTime.getTime() < globalStartTime.getTime()) {
      return globalEndTime;
    }
    
    // Ensure the adjusted time is not after the original global end time
    if (!hasLiveStreams && adjustedEndTime.getTime() > globalEndTime.getTime()) {
      return globalEndTime;
    }
    
    // Use liveTimeUpdate to ensure recalculation (value doesn't affect result)
    void liveTimeUpdate;
    
    return adjustedEndTime;
  }, [selectedMap, hasLiveStreams, globalStartTime, globalEndTime, liveTimeUpdate, liveEdgeSafetyBuffer]);

  // Compute allowed overlap duration in seconds (uses adjusted end time for live streams in full video mode)
  const maxAllowedSeconds = useMemo(() => {
    const effectiveEndTime = timelineGlobalEndTime || globalEndTime;
    if (!globalStartTime || !effectiveEndTime) return 0;
    return Math.max(0, Math.floor((effectiveEndTime.getTime() - globalStartTime.getTime()) / 1000));
  }, [globalStartTime, timelineGlobalEndTime, globalEndTime]);


  // Create youtube_vods array for Notes component with correct per-track session offset
  const youtube_vods: YoutubeVod[] = React.useMemo(() => {
    return tracks.map(track => {
      let seconds_from_session_start = 0;
      if (globalStartTime && track.yt_stream_start_utc) {
        const baseStart = new Date(track.yt_stream_start_utc);
        const offsetSeconds = track.yt_stream_sync_offset ?? 0;
        const adjustedStartMs = baseStart.getTime() + offsetSeconds * 1000;
        seconds_from_session_start = (adjustedStartMs - globalStartTime.getTime()) / 1000;
      }
      return {
        youtube_id: track.youtube_id,
        seconds_from_session_start
      };
    });
  }, [tracks, globalStartTime]);

  // Get the active youtube_id based on current active video
  const active_youtube_id = React.useMemo(() => {
    if (videoInfos.length > 0 && activeVideoIndex < videoInfos.length) {
      return videoInfos[activeVideoIndex].id;
    }
    return '';
  }, [videoInfos, activeVideoIndex]);

  // Compute processing status of the main track early for polling logic
  const isProcessing = useMemo(() => {
    if (tracks.length === 0) return false;
    const idx1 = tracks.find(t => t.track_index === 1);
    const mt = idx1 ? idx1 : tracks.slice().sort((a, b) => a.track_index - b.track_index)[0];
    return mt?.processing_status === 'PROCESSING';
  }, [tracks]);
 
  // Helper function to calculate global UTC times for rounds
  const calculateGlobalRoundTimes = (tracks: Track[]): Track[] => {
    return tracks.map(track => {
      if (!track.rounds || !track.yt_stream_start_utc) {
        return track;
      }

      const streamStartTime = new Date(track.yt_stream_start_utc);
      const offsetSeconds = track.yt_stream_sync_offset ?? 0;
      const adjustedStartTime = new Date(streamStartTime.getTime() + offsetSeconds * 1000);
      
      const roundsWithGlobalTimes: Round[] = track.rounds.map(round => ({
        ...round,
        globalStartTime: new Date(adjustedStartTime.getTime() + (round.round_start * 1000)),
        globalEndTime: new Date(adjustedStartTime.getTime() + (round.round_end * 1000))
      }));

      return {
        ...track,
        rounds: roundsWithGlobalTimes
      };
    });
  };

  // Helper function to determine if a track is live (missing end time)
  const isLiveTrack = (track: Track): boolean => {
    return track.yt_stream_start_utc !== null && track.yt_stream_end_utc === null && track.track_index === 1;
  };

  // Helper function to get effective end time for a track (current time for live streams)
  const getEffectiveEndTime = (track: Track): Date | null => {
    if (track.yt_stream_end_utc) {
      const endTime = new Date(track.yt_stream_end_utc);
      const offsetSeconds = track.yt_stream_sync_offset ?? 0;
      return new Date(endTime.getTime() + offsetSeconds * 1000);
    } else if (track.yt_stream_start_utc && track.track_index === 1) {
      // Live stream - use current UTC time as end time
      return new Date();
    }
    return null;
  };

  // Helper function to process tracks with live stream support
  const processTracksWithLiveSupport = (tracksData: Track[]): Track[] => {
    return tracksData.map(track => {
      if (isLiveTrack(track)) {
        // For live tracks, set the current UTC time as end time
        return {
          ...track,
          yt_stream_end_utc: new Date().toISOString()
        };
      }
      return track;
    });
  };

  // Map processing status/visuals similar to ScrimTracks.tsx
  const getProcessingStatusDisplay = (status?: string, error?: string) => {
    if (!status) {
      return { 
        text: 'QUEUING', 
        showSpinner: true, 
        spinnerVariant: 'secondary' as const,
        animationType: 'border' as const,
        className: styles.statusText 
      };
    }
    switch (status) {
      case 'QUEUED':
        return { text: 'Queued', showSpinner: false, className: styles.statusText };
      case 'LAUNCHED':
        return { text: 'Launching', showSpinner: true, spinnerVariant: 'primary' as const, animationType: 'border' as const, className: styles.statusText };
      case 'INITIALIZING':
        return { text: 'Initializing', showSpinner: true, spinnerVariant: 'info' as const, animationType: 'border' as const, className: styles.statusText };
      case 'CONNECTING':
        return { text: 'Connecting', showSpinner: true, spinnerVariant: 'warning' as const, animationType: 'border' as const, className: styles.statusText };
      case 'PROCESSING':
        return { text: 'Processing', showSpinner: true, spinnerVariant: 'info' as const, animationType: 'grow' as const, className: styles.statusText };
      case 'COMPLETED':
        return { text: 'Completed', showSpinner: false, className: styles.statusTextCompleted };
      case 'ERROR':
        return { text: 'Error', showSpinner: false, className: styles.statusTextError };
      default:
        return { text: status, showSpinner: false, className: styles.statusText };
    }
  };

  // API fetch + basic response validation (no stateful processing)
  const fetchTracksFromApi = async (showLoading: boolean = true): Promise<Track[]> => {
    if (!scrim_id) return [];
    try {
      if (showLoading) setIsLoading(true);
      setError('');
      const response = await getScrimTracks(scrim_id);
      const tracksData: Partial<Track>[] = response.tracks || [];
      const validTracks = tracksData
        .filter((track: Partial<Track>) => track.yt_stream_start_utc !== null)
        .map((t: Partial<Track>) => ({
          ...t,
          yt_stream_sync_offset: t.yt_stream_sync_offset ?? 0
        } as Track));
      if (validTracks.length === 0) {
        throw new Error('No valid tracks found for playback');
      }
      return validTracks;
    } finally {
      if (showLoading) setIsLoading(false);
    }
  };

  // Stateful processing of tracks (can be reused with local modifications)
  const processTracks = (inputTracks: Track[]) => {
    // Check if we have any live streams
    const liveStreams = inputTracks.filter(isLiveTrack);
    setHasLiveStreams(liveStreams.length > 0);

    // Process tracks to handle live streams (set current time as end time)
    const processedTracks = processTracksWithLiveSupport(inputTracks);

    // Calculate global times for rounds
    const finalTracks = calculateGlobalRoundTimes(processedTracks);

    // Update tracks state
    setTracks(finalTracks);

    // Compute overlap window: start at latest start, end at earliest end
    const startTimes = finalTracks
      .map((track: Track) => {
        const baseTime = new Date(track.yt_stream_start_utc!);
        const offsetSeconds = track.yt_stream_sync_offset ?? 0;
        return new Date(baseTime.getTime() + offsetSeconds * 1000);
      })
      .sort((a: Date, b: Date) => a.getTime() - b.getTime());
    const latestStart = startTimes[startTimes.length - 1];
    let latestEnd = new Date();
    const firstIndexTrack = finalTracks.findIndex(t => t.track_index === 1);
    if (firstIndexTrack !== -1) {
        latestEnd = getEffectiveEndTime(finalTracks[firstIndexTrack]) || new Date();
    }

    setGlobalStartTime(latestStart);
    setGlobalEndTime(latestEnd);

    // Create VideoInfo objects
    const videos: VideoInfo[] = finalTracks.map((track: Track) => {
      const baseStartTime = new Date(track.yt_stream_start_utc!);
      const offsetSeconds = track.yt_stream_sync_offset ?? 0;
      const startTime = new Date(baseStartTime.getTime() + offsetSeconds * 1000);
      const endTime = getEffectiveEndTime(track) || new Date();
      const durationMs = endTime.getTime() - startTime.getTime();
      const durationSeconds = Math.floor(durationMs / 1000);
      return {
        id: track.youtube_id,
        startTime,
        duration: durationSeconds,
        title: `${track.yt_channel}: ${track.yt_title}${isLiveTrack(track) ? ' (LIVE)' : ''}`
      };
    });

    setVideoInfos(videos);

    // Determine if we have any processed rounds
    const anyRounds = finalTracks.some(t => (t.rounds?.length || 0) > 0);
    setHasProcessedRounds(anyRounds);

    // Record whether rounds existed on the first load only once
    if (hadRoundsAtFirstLoadRef.current === null) {
      hadRoundsAtFirstLoadRef.current = anyRounds;
    }

    // Apply initial default (Map 1 / Round 1) only once
    if (anyRounds && !didApplyInitialDefaultRef.current) {
      // Prefer map 1 round 1; fallback to the first map's first round
      const allRounds: Round[] = finalTracks.flatMap(t => t.rounds || []);
      if (allRounds.length > 0) {
        let targetRound = allRounds.find(r => r.map_index === 1 && r.round_number === 1);
        if (!targetRound) {
          const firstMapIndex = Math.min(...Array.from(new Set(allRounds.map(r => r.map_index))));
          const firstMapRounds = allRounds
            .filter(r => r.map_index === firstMapIndex)
            .sort((a, b) => a.round_number - b.round_number);
          targetRound = firstMapRounds[0];
        }

        if (targetRound) {
          // If rounds existed on initial load, be aggressive: set round and seek
          if (hadRoundsAtFirstLoadRef.current) {
            setSelectedMap(targetRound.map_index);
            setSelectedRound(targetRound.round_number);

            // Make the video that contains this round the active one
            const trackIndexWithRound = finalTracks.findIndex(t => t.rounds?.some(r => r.map_index === targetRound!.map_index && r.round_number === targetRound!.round_number));
            if (trackIndexWithRound >= 0) {
              setActiveVideoIndex(trackIndexWithRound);
            }

            // Seek global time to the round start, clamped to overlap window
            if (targetRound.globalStartTime) {
              const overlapSeconds = Math.max(0, Math.floor((latestEnd.getTime() - latestStart.getTime()) / 1000));
              const secondsFromGlobalStart = Math.max(
                0,
                Math.min(
                  (targetRound.globalStartTime.getTime() - latestStart.getTime()) / 1000,
                  overlapSeconds
                )
              );
              handleSeek(secondsFromGlobalStart);
              initialRoundStartSecondsRef.current = secondsFromGlobalStart;
            }
          } else {
            // Rounds appeared later (after initial load): only set the map, do not select a round or seek
            setSelectedMap(targetRound.map_index);
            setSelectedRound(null);
          }

          didApplyInitialDefaultRef.current = true;
        }
      }
    }

    // Mark initial ready once initial data is set (only once)
    setIsInitialReady(prev => prev || true);
  };

  // Orchestrator that calls API fetch then processing
  const loadScrimTracks = async (showLoading: boolean = true) => {
    if (!scrim_id) return;
    try {
      const validTracks = await fetchTracksFromApi(showLoading);
      processTracks(validTracks);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tracks');
    }
  };

  useEffect(() => {
    if (scrim_id) {
      loadScrimTracks();
    }
  }, [scrim_id]);

  // Restore state after reload (due to quality change)
  useEffect(() => {
    if (!scrim_id) return;
    if (videoInfos.length === 0 || !globalStartTime || !globalEndTime) return;

    const restoreKey = `scrim_restore_state_${scrim_id}`;
    const raw = localStorage.getItem(restoreKey);
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw);
      const { activeVideoIndex: savedActive, selectedMap: savedMap, selectedRound: savedRound, currentTime: savedTime } = parsed || {};

      if (typeof savedActive === 'number' && savedActive >= 0 && savedActive < videoInfos.length) {
        setActiveVideoIndex(savedActive);
      }

      if (savedMap === null || typeof savedMap === 'number') {
        setSelectedMap(savedMap);
      }

      if (savedRound === null || typeof savedRound === 'number') {
        setSelectedRound(savedRound);
      }

      if (typeof savedTime === 'number' && savedTime >= 0) {
        // Seek to saved global time, clamped to overlap
        const clamped = Math.max(0, Math.min(savedTime, maxAllowedSeconds));
        handleSeek(clamped);
      }
    } catch (e) {
      // ignore
    } finally {
      localStorage.removeItem(restoreKey);
    }
  }, [scrim_id, videoInfos.length, globalStartTime, globalEndTime, maxAllowedSeconds]);

  // Close quality menu on outside click or Escape
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (qualityMenuRef.current && !qualityMenuRef.current.contains(e.target as Node)) {
        setIsQualityMenuOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsQualityMenuOpen(false);
    };
    if (isQualityMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isQualityMenuOpen]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      
      // For 'n' key, allow it unless we're already in the notes textarea
      if (e.key && e.key.toLowerCase() === 'n') {
        // Check if we're in the notes textarea specifically
        const isInNotesTextarea = target && target.classList.contains('editTextarea');
        if (!isInNotesTextarea) {
          e.preventDefault();
          // Only pause playback if not in freeze mode (Full Video mode)
          if (selectedMap !== null) {
            setIsPlaying(false); // Pause playback only in round-specific mode
          }
          // Dispatch custom event to focus notes textarea
          const focusNotesEvent = new CustomEvent('focusNotesTextarea');
          window.dispatchEvent(focusNotesEvent);
          return;
        }
      }

      // Ignore other keyboard controls if user is typing in an input field
      if (target && (
        target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.contentEditable === 'true' ||
        target.isContentEditable
      )) {
        return;
      }

      // Prevent default behavior for Space key to avoid page scrolling
      if (e.code === 'Space') {
        e.preventDefault();
        handlePlayPause();
      }
      
      // Handle number keys 1-6 for active track switching
      if (e.code >= 'Digit1' && e.code <= 'Digit6') {
        const trackIndex = parseInt(e.code.replace('Digit', '')) - 1; // Convert Digit1 -> 0, Digit2 -> 1, etc.
        if (trackIndex >= 0 && trackIndex < videoInfos.length) {
          handleActiveVideoChange(trackIndex);
        }
      }
      
      // Handle arrow keys for 5-second seeking (only when Shift is not pressed)
      if (e.code === 'ArrowLeft' && !e.shiftKey) {
        e.preventDefault();
        const newTime = Math.max(0, currentTime - 5);
        handleSeek(newTime);
      }
      
      if (e.code === 'ArrowRight' && !e.shiftKey) {
        e.preventDefault();
        const newTime = Math.min(maxAllowedSeconds, currentTime + 5);
        handleSeek(newTime);
      }
      
      // Handle Shift + arrow keys for round navigation
      if (e.code === 'ArrowLeft' && e.shiftKey) {
        e.preventDefault();
        handlePreviousRound();
      }
      
      if (e.code === 'ArrowRight' && e.shiftKey) {
        e.preventDefault();
        handleNextRound();
      }
      
      // Handle M key for mute toggle
      if (e.code === 'KeyM') {
        e.preventDefault();
        handleMuteToggle();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [playersReady, isPlaying, currentTime, videoInfos.length, maxAllowedSeconds, hasProcessedRounds, selectedMap, selectedRound, tracks, volume, prevVolume]); // Dependencies for all handlers

  // Poll for processing status every 10s until we have rounds
  useEffect(() => {
    if (!scrim_id) return;
    if (hasProcessedRounds) return;
    const interval = setInterval(() => {
      loadScrimTracks(false);
    }, 10000);
    return () => clearInterval(interval);
  }, [scrim_id, hasProcessedRounds]);

  // Once we have rounds but processing continues, refresh once per minute
  useEffect(() => {
    if (!scrim_id) return;
    if (!hasProcessedRounds) return; // handled by 10s poller above
    if (!isProcessing) return;
    const interval = setInterval(() => {
      loadScrimTracks(false);
    }, 60000);
    return () => clearInterval(interval);
  }, [scrim_id, hasProcessedRounds, isProcessing]);

  // Update live streams timeline every 30 seconds
  useEffect(() => {
    if (!hasLiveStreams) return;
    
    const interval = setInterval(() => {
      // Simply reload tracks data which will recalculate everything
      loadScrimTracks(false);
    }, 30000); // Update every 30 seconds
    
    liveUpdateIntervalRef.current = interval;
    return () => {
      if (liveUpdateIntervalRef.current) {
        clearInterval(liveUpdateIntervalRef.current);
        liveUpdateIntervalRef.current = null;
      }
    };
  }, [hasLiveStreams]);

  // Update timeline end time every second when in full video mode with live streams
  // This ensures the liveEdgeSafetyBuffer is maintained as time passes
  useEffect(() => {
    if (!hasLiveStreams || selectedMap !== null) return;
    
    const interval = setInterval(() => {
      setLiveTimeUpdate(Date.now());
    }, 1000); // Update every second
    
    return () => clearInterval(interval);
  }, [hasLiveStreams, selectedMap]);

  // Cleanup live update interval on unmount
  useEffect(() => {
    return () => {
      if (liveUpdateIntervalRef.current) {
        clearInterval(liveUpdateIntervalRef.current);
        liveUpdateIntervalRef.current = null;
      }
    };
  }, []);

  // Removed late initial default effect; handled during initial data load above

  const handlePlayPause = () => {
    if (!playersReady) return;
    // On first play, ensure we start from the initial round start if available
    if (!isPlaying && initialRoundStartSecondsRef.current != null) {
      const target = initialRoundStartSecondsRef.current;
      if (Math.abs(currentTime - target) > 0.5) {
        handleSeek(target);
      }
      initialRoundStartSecondsRef.current = null; // consume once
    }
    setIsPlaying(!isPlaying);
  };

  function handleSeek(time: number) {
    const clamped = globalStartTime && globalEndTime
      ? Math.max(0, Math.min(time, maxAllowedSeconds))
      : time;
    setCurrentTime(clamped);
    setSeekTargetTime(clamped);
    setIsSeekingToTarget(true);
    isSeekingToTargetRef.current = true; // Update ref immediately
    lastSeekAtRef.current = Date.now();
    
    // Clear any existing timeout
    if (seekTimeoutRef.current) {
      clearTimeout(seekTimeoutRef.current);
    }
    // No timeout fallback: we will clear seeking state only when player catches up
  }

  const handleTimelineDragStart = () => {
    setIsTimelineDragging(true);
    isTimelineDraggingRef.current = true; // Update ref immediately
    setWasPlayingBeforeDrag(isPlaying);
    setIsPlaying(false); // Pause playback when dragging starts
  };

  const handleTimelineDragEnd = () => {
    setIsTimelineDragging(false);
    isTimelineDraggingRef.current = false; // Update ref immediately
    
    // Small delay to ensure seeking completes before resuming playback
    if (wasPlayingBeforeDrag) {
      setTimeout(() => {
        setIsPlaying(true); // Resume playback if it was playing before dragging
      }, 200);
    }
  };

  const handlePlayersReady = useCallback((ready: boolean) => {
    setPlayersReady(ready);
  }, []);

  const handleTimeUpdate = useCallback((time: number) => {
    const isDragging = isTimelineDraggingRef.current;
    const isSeeking = isSeekingToTargetRef.current;
    
    // Only update time from players when timeline is not being dragged
    if (isDragging) {
      return;
    }

    // If we're seeking to a target, check if the player has caught up
    const veryRecentSeek = Date.now() - lastSeekAtRef.current < 2000; // 2s window check for live streams
    if (isSeeking && (!hasLiveStreams || veryRecentSeek)) {
      const timeDiff = Math.abs(time - seekTargetTime);
      if (timeDiff > 2.0) {
        // Player hasn't caught up yet, ignore this time update
        return;
      } else {
        // Player has caught up, resume normal time updates
        setIsSeekingToTarget(false);
        isSeekingToTargetRef.current = false; // Update ref immediately
      }
    }

    // After a recent seek, ignore backward jumps that would regress before the seek target
    const recentSeek = Date.now() - lastSeekAtRef.current < 10000; // 10s window
    if (recentSeek && time < seekTargetTime - 2.0 && !hasLiveStreams) {
      return;
    }
    
    // Clamp to overlap window
    if (globalStartTime && globalEndTime && !hasLiveStreams) {
      const clamped = Math.max(0, Math.min(time, maxAllowedSeconds));
      setCurrentTime(clamped);
      // Optionally pause at the end of overlap
      if (clamped > maxAllowedSeconds) {
        setIsPlaying(false);
      }
    } else {
      setCurrentTime(time);
    }
  }, [seekTargetTime, globalStartTime, globalEndTime, maxAllowedSeconds]); // Simplified dependencies - refs don't need to be in deps

  const handleViewModeToggle = () => {
    setViewMode(prev => prev === 'grid' ? 'single' : 'grid');
  };

  const handleActiveVideoChange = (index: number) => {
    setActiveVideoIndex(index);
  };

  const handlePreviousRound = () => {
    if (!hasProcessedRounds || selectedMap == null) return;
    
    const rounds = getVideoRounds();
    const roundsForCurrentMap = rounds.filter(r => r.map_index === selectedMap).sort((a, b) => a.round_number - b.round_number);
    
    if (roundsForCurrentMap.length === 0) return;
    
    let targetRound: Round;
    
    if (selectedRound == null) {
      // If no round is selected, go to the last round of the current map
      targetRound = roundsForCurrentMap[roundsForCurrentMap.length - 1];
    } else {
      // Find the current round index
      const currentRoundIndex = roundsForCurrentMap.findIndex(r => r.round_number === selectedRound);
      if (currentRoundIndex <= 0) {
        // If we're at the first round or round not found, go to the last round
        targetRound = roundsForCurrentMap[roundsForCurrentMap.length - 1];
      } else {
        // Go to the previous round
        targetRound = roundsForCurrentMap[currentRoundIndex - 1];
      }
    }
    
    handleRoundChange(targetRound);
  };

  const handleNextRound = () => {
    if (!hasProcessedRounds || selectedMap == null) return;
    
    const rounds = getVideoRounds();
    const roundsForCurrentMap = rounds.filter(r => r.map_index === selectedMap).sort((a, b) => a.round_number - b.round_number);
    
    if (roundsForCurrentMap.length === 0) return;
    
    let targetRound: Round;
    
    if (selectedRound == null) {
      // If no round is selected, go to the first round of the current map
      targetRound = roundsForCurrentMap[0];
    } else {
      // Find the current round index
      const currentRoundIndex = roundsForCurrentMap.findIndex(r => r.round_number === selectedRound);
      if (currentRoundIndex === -1 || currentRoundIndex >= roundsForCurrentMap.length - 1) {
        // If round not found or we're at the last round, go to the first round
        targetRound = roundsForCurrentMap[0];
      } else {
        // Go to the next round
        targetRound = roundsForCurrentMap[currentRoundIndex + 1];
      }
    }
    
    handleRoundChange(targetRound);
  };

  const handleMapChange = (mapIndex: number | null) => {
    setSelectedMap(mapIndex);
    setSelectedRound(null); // Reset round selection when map changes
  };

  const handleRoundChange = (round: Round) => {
    setSelectedRound(round.round_number);
    
    if (round.globalStartTime && globalStartTime) {
      // Convert global round start time to seconds from global timeline start
      let globalTimelineSeconds = (round.globalStartTime.getTime() - globalStartTime.getTime()) / 1000;
      // Clamp to overlap window
      if (globalEndTime) {
        globalTimelineSeconds = Math.max(0, Math.min(globalTimelineSeconds, maxAllowedSeconds));
      } else {
        globalTimelineSeconds = Math.max(0, globalTimelineSeconds);
      }
      handleSeek(globalTimelineSeconds);
    }

    // Always start playback when navigating to a round
    setIsPlaying(true);
  };

  // Handle note click from Notes sidebar
  const handleNoteClick = (globalTime: number, roundNumber?: number, youtubeId?: string) => {
    // Determine absolute time using the active video's start time
    let effectiveActiveIndex = activeVideoIndex;
    if (youtubeId) {
      const idx = videoInfos.findIndex(v => v.id === youtubeId);
      if (idx >= 0) {
        effectiveActiveIndex = idx;
        setActiveVideoIndex(idx);
      }
    }
    let activeInfo = videoInfos[effectiveActiveIndex];
    if (!activeInfo || !globalStartTime) return;

    // If we are in round mode and a round number is provided, switch to that round
    if (selectedMap != null && roundNumber) {
      setSelectedRound(roundNumber);
    }

    handleSeek(globalTime);
    setIsPlaying(true);
  };

  const handleVolumeChange = (newVolume: number) => {
    setVolume(newVolume);
  };

  const handleMuteToggle = () => {
    if (volume > 0) {
      // Currently unmuted, so mute
      setPrevVolume(volume);
      setVolume(0);
    } else {
      // Currently muted, so unmute to previous volume
      setVolume(prevVolume);
    }
  };

  const handleSettingsSave = (newBuffer: number) => {
    setLiveEdgeSafetyBuffer(newBuffer);
    
    // Calculate the new boundary immediately to clamp current time
    // This matches the logic in timelineGlobalEndTime useMemo
    let newMaxAllowedSeconds = maxAllowedSeconds;
    if (hasLiveStreams && selectedMap === null && globalStartTime && globalEndTime) {
      // Calculate the new adjusted end time with the new buffer
      const now = new Date();
      const adjustedEndTime = new Date(now.getTime() - newBuffer * 1000);
      
      // Match the validation logic from timelineGlobalEndTime
      if (adjustedEndTime.getTime() < globalStartTime.getTime()) {
        // Adjusted time is before start, use original end time
        newMaxAllowedSeconds = Math.max(0, Math.floor((globalEndTime.getTime() - globalStartTime.getTime()) / 1000));
      } else if (adjustedEndTime.getTime() > globalEndTime.getTime()) {
        // Adjusted time is after original end, use original end time
        newMaxAllowedSeconds = Math.max(0, Math.floor((globalEndTime.getTime() - globalStartTime.getTime()) / 1000));
      } else {
        // Adjusted time is valid, use it
        newMaxAllowedSeconds = Math.max(0, Math.floor((adjustedEndTime.getTime() - globalStartTime.getTime()) / 1000));
      }
    }
    
    // Clamp current time to the new boundary
    const clampedTime = Math.max(0, Math.min(currentTime, newMaxAllowedSeconds));
    if (clampedTime !== currentTime) {
      handleSeek(clampedTime);
    }
    
    // Force a recalculation by triggering liveTimeUpdate if we have live streams
    if (hasLiveStreams) {
      setLiveTimeUpdate(Date.now());
    }
  };

  const applyQualityChange = (newQuality: VideoQuality) => {
    if (newQuality === videoQuality) {
      setIsQualityMenuOpen(false);
      return;
    }
    const confirmed = window.confirm('Changing video quality requires a page reload. Apply now?');
    if (!confirmed) {
      setIsQualityMenuOpen(false);
      return;
    }

    // Persist new quality
    localStorage.setItem('scrim_video_quality', newQuality);

    // Save current session state for restore after reload
    if (scrim_id) {
      const restoreKey = `scrim_restore_state_${scrim_id}`;
      const state = {
        activeVideoIndex,
        selectedMap,
        selectedRound,
        currentTime,
      };
      localStorage.setItem(restoreKey, JSON.stringify(state));
    }

    // Reload page to apply
    window.location.reload();
  };

  const handleRestartProcessing = async () => {
    if (!scrim_id) return;
    
    try {
      setIsRestartingProcessing(true);
      await launchScrim(scrim_id);
      setShowRestartModal(false);
      // Reload tracks to get updated status
      await loadScrimTracks(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to restart processing');
    } finally {
      setIsRestartingProcessing(false);
    }
  };

  const handleTrackSyncChange = async (videoId: string, offsetChange: number) => {
    if (!scrim_id) return;
    
    // Find the track by videoId
    const track = tracks.find(t => t.youtube_id === videoId);
    if (!track) return;
    
    const isFirstTrack = track.track_index === 1;
    
    // Optimistically update local state immediately for responsiveness
    const updatedTracks = tracks.map(t => {
      if (isFirstTrack) {
        // For first track: don't change its offset, instead move all other tracks in inverse direction
        if (t.track_index === 1) {
          // Keep first track's offset unchanged
          return t;
        } else {
          // Apply inverse offset to all other tracks
          const currentOffset = t.yt_stream_sync_offset ?? 0;
          const newOffset = currentOffset - offsetChange;
          return { ...t, yt_stream_sync_offset: newOffset };
        }
      } else {
        // For non-first tracks: apply offset change normally
        if (t.youtube_id === videoId) {
          const currentOffset = t.yt_stream_sync_offset ?? 0;
          const newOffset = currentOffset + offsetChange;
          return { ...t, yt_stream_sync_offset: newOffset };
        }
        return t;
      }
    });
    setTracks(updatedTracks);
    processTracks(updatedTracks);

    // If syncing first track, seek the video position by the offset amount
    if (isFirstTrack) {
      const newTime = currentTime - offsetChange;
      handleSeek(newTime);
    }

    // Fire-and-forget backend persistence
    if (isFirstTrack) {
      // Update all other tracks with their new offsets
      updatedTracks.forEach(t => {
        if (t.track_index !== 1) {
          void updateScrimTrackOffset(scrim_id, t.youtube_id, t.yt_stream_sync_offset ?? 0).catch(err => {
            console.error('Failed to persist track sync offset:', err);
          });
        }
      });
    } else {
      // Update the single track that was changed
      const updatedTrack = updatedTracks.find(t => t.youtube_id === videoId);
      if (updatedTrack) {
        void updateScrimTrackOffset(scrim_id, videoId, updatedTrack.yt_stream_sync_offset ?? 0).catch(err => {
          console.error('Failed to persist track sync offset:', err);
        });
      }
    }
  };

  // Get rounds from the track with track_index == 1, or return empty if not found
  const getVideoRounds = (): Round[] => {
    if(!tracks || tracks.length === 0) return [];
    const index1Track = tracks.find(track => track.track_index === 1);
    return index1Track?.rounds || [];
  };

  // Extract existing youtubeIDs and stream start times for AddScrimTracks modal
  const existingYoutubeIds = React.useMemo(() => {
    return tracks.map(t => t.youtube_id);
  }, [tracks]);

  const existingStreamStartTimes = React.useMemo(() => {
    return tracks
      .filter(t => t.yt_stream_start_utc)
      .map(t => new Date(t.yt_stream_start_utc!)); // Use raw stream start times for validation
  }, [tracks]);

  // Determine processing status of main (track_index 1) track
  const mainTrack = useMemo(() => {
    const idx1 = tracks.find(t => t.track_index === 1);
    if (idx1) return idx1;
    return tracks.slice().sort((a, b) => a.track_index - b.track_index)[0];
  }, [tracks]);
  const statusDisplay = getProcessingStatusDisplay(mainTrack?.processing_status, mainTrack?.processing_error);

  // Last map index among available rounds
  const lastMapIndex = useMemo(() => {
    const rounds = getVideoRounds();
    if (!rounds || rounds.length === 0) return null;
    return Math.max(...rounds.map(r => r.map_index));
  }, [tracks]);
 
  // Compute selected round absolute range in seconds from globalStartTime
  const selectedRoundRange = useMemo(() => {
    if (!hasProcessedRounds || selectedMap == null || selectedRound == null) return null;
    if (!globalStartTime) return null;
    const rounds = getVideoRounds();
    const r = rounds.find(rr => rr.map_index === selectedMap && rr.round_number === selectedRound);
    if (!r || !r.globalStartTime || !r.globalEndTime) return null;
    const startSec = (r.globalStartTime.getTime() - globalStartTime.getTime()) / 1000;
    const endSec = (r.globalEndTime.getTime() - globalStartTime.getTime()) / 1000;
    return { startSec: Math.max(0, startSec), endSec: Math.max(0, endSec) };
  }, [hasProcessedRounds, selectedMap, selectedRound, globalStartTime, tracks]);

  // Build Notes roundInfo when a map/round is selected (not full video)
  const notesRoundInfo = useMemo(() => {
    if (!hasProcessedRounds) return undefined;
    if (selectedMap == null) return undefined;

    // Always use track with index 1 for rounds
    const index1Track = tracks.find(t => t.track_index === 1);
    const roundsSource = (index1Track && index1Track.rounds && index1Track.rounds.length > 0)
      ? index1Track.rounds
      : getVideoRounds();

    if (!roundsSource || roundsSource.length === 0) return undefined;

    // Get seconds_from_session_start for the index 1 track from youtube_vods
    const index1Vod = index1Track ? youtube_vods.find(vod => vod.youtube_id === index1Track.youtube_id) : undefined;
    const secondsFromSessionStart = index1Vod?.seconds_from_session_start ?? 0;

    const roundsForMap = roundsSource
      .filter(r => r.map_index === selectedMap)
      .map(r => ({
        round_number: r.round_number,
        round_start: r.round_start + secondsFromSessionStart,
        round_end: r.round_end + secondsFromSessionStart,
      }));

    if (roundsForMap.length === 0) return undefined;

    // Compute current local time relative to active video and current round start
    let currentLocalTime = 0;
    try {
      if (globalStartTime && videoInfos[activeVideoIndex]) {
        const activeVideoStart = videoInfos[activeVideoIndex].startTime;
        const absoluteMs = globalStartTime.getTime() + currentTime * 1000;
        const activeElapsedSec = Math.max(0, (absoluteMs - activeVideoStart.getTime()) / 1000);
        const currentRoundData = roundsSource.find(r => r.map_index === selectedMap && r.round_number === selectedRound);
        if (currentRoundData) {
          currentLocalTime = Math.max(0, activeElapsedSec - currentRoundData.round_start);
        }
      }
    } catch {}

    return {
      currentRound: selectedRound ?? 1,
      currentLocalTime,
      rounds: roundsForMap,
    };
  }, [hasProcessedRounds, selectedMap, selectedRound, tracks, activeVideoIndex, videoInfos, globalStartTime, currentTime, youtube_vods]);

  if (isLoading || !isInitialReady) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          Loading scrim...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <i className="bi bi-exclamation-triangle"></i>
          <div>
            <h3>Error loading scrim</h3>
            <p>{error}</p>
            <button onClick={() => { void loadScrimTracks(); }} className={styles.retryButton}>
              Retry
            </button>
            <button 
              onClick={() => navigate('/scrims')} 
              className={styles.backButton}
            >
              Back to Scrims
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.scrimContent}>
        <div className={styles.videoContainer}>
          <div className={styles.playersWrapper}>
            <div className={styles.playersContainer}>
              <ScrimYoutubePlayers
                videoInfos={videoInfos}
                globalStartTime={globalStartTime}
                isPlaying={isPlaying}
                currentTime={currentTime}
                viewMode={viewMode}
                activeVideoIndex={activeVideoIndex}
                onActiveVideoChange={handleActiveVideoChange}
                onPlayersReady={handlePlayersReady}
                onTimeUpdate={handleTimeUpdate}
                quality={videoQuality}
                onRequestPlay={() => {
                  if (initialRoundStartSecondsRef.current != null) {
                    const target = initialRoundStartSecondsRef.current;
                    if (Math.abs(currentTime - target) > 0.5) {
                      handleSeek(target);
                    }
                    initialRoundStartSecondsRef.current = null;
                  }
                  setIsPlaying(true);
                }}
                allReady={playersReady}
                volume={volume}
                onTrackSyncChange={handleTrackSyncChange}
                onAddTrackClick={() => setShowAddTracksModal(true)}
              />
            </div>
          </div>
          <Notes 
            youtube_vods={youtube_vods}
            active_youtube_id={active_youtube_id}
            currentTime={currentTime}
            onExpandedChange={() => {}}
            roundInfo={notesRoundInfo}
            onNoteClick={handleNoteClick}
            authState={authState}
            isPlaying={isPlaying}
            onPlaybackControl={setIsPlaying}
            freezeTimeForNewNote={selectedMap === null} // Freeze timestamp when in "Full Video" mode
          />
        </div>

        <div className={styles.controlsContainer}>
          <div className={styles.navigationContainer}>
            <div className={styles.navigationLeft}>
              {hasProcessedRounds && (
                <ScrimMapNavigation
                  rounds={getVideoRounds()}
                  selectedMap={selectedMap}
                  onMapChange={handleMapChange}
                />
              )}
            </div>
            <div className={styles.navigationRight}>
              {hasProcessedRounds ? (
                <ScrimRoundNavigation
                  rounds={getVideoRounds()}
                  selectedMap={selectedMap}
                  selectedRound={selectedRound}
                  onRoundChange={handleRoundChange}
                  isProcessing={isProcessing}
                  isLastMap={selectedMap !== null && lastMapIndex !== null && selectedMap === lastMapIndex}
                />
              ) : (
                <div className={`${styles.statusInline} ${mainTrack?.processing_status === 'ERROR' ? styles.statusInlineError : ''}`}>
                  {statusDisplay.showSpinner && (
                    <Spinner
                      as="span"
                      animation={statusDisplay.animationType}
                      size="sm"
                      className={styles.statusSpinner}
                    />
                  )}
                  <span className={`${styles.statusPrefix} ${mainTrack?.processing_status === 'ERROR' ? styles.statusPrefixError : ''}`}>
                    No processed rounds yet.
                  </span>
                  <span className={`${statusDisplay.className} ${mainTrack?.processing_status === 'ERROR' ? styles.statusTextErrorTertiary : ''}`}>
                    Status: <b>{statusDisplay.text}</b>
                  </span>
                  {mainTrack?.processing_status === 'ERROR' && (
                    <button
                      className={styles.restartButton}
                      onClick={() => setShowRestartModal(true)}
                      title="Restart processing"
                    >
                      Restart
                    </button>
                  )}
                </div>
              )}
            </div>
            <ScrimHotkeysLabel />
            {videoInfos.length > 1 && (
              <button 
                  className={styles.viewToggleButton}
                  onClick={handleViewModeToggle}
                  title={`Switch to ${viewMode === 'grid' ? 'single' : 'grid'} view`}
                >
                  <i className={`bi ${viewMode === 'grid' ? 'bi-pip' : 'bi-grid-3x3'}`}></i>
                </button>
            )}
            {/* Quality dropup */}
            <div className={styles.qualityDropup} ref={qualityMenuRef}>
              <button
                className={styles.qualityButton}
                onClick={() => setIsQualityMenuOpen(prev => !prev)}
                title="Video quality"
              >
                {videoQuality === 'hd1080' ? '1080p' : '720p'}
              </button>
              {isQualityMenuOpen && (
                <div className={styles.qualityMenu}>
                  <button
                    className={`${styles.qualityMenuItem} ${videoQuality === 'hd1080' ? styles.active : ''}`}
                    onClick={() => applyQualityChange('hd1080')}
                  >
                    1080p
                  </button>
                  <button
                    className={`${styles.qualityMenuItem} ${videoQuality === 'hd720' ? styles.active : ''}`}
                    onClick={() => applyQualityChange('hd720')}
                  >
                    720p
                  </button>
                </div>
              )}
            </div>
            {/* Settings button */}
            <button
              className={styles.settingsButton}
              onClick={() => setShowSettingsModal(true)}
              title="Settings"
            >
              <i className="bi bi-gear"></i>
            </button>
          </div>
          <div className={styles.timelineVolumeContainer}>
            <ScrimTimeline
              videoInfos={videoInfos}
              globalStartTime={globalStartTime}
              currentTime={currentTime}
              onSeek={handleSeek}
              isPlaying={isPlaying}
              isSeekingToTarget={isSeekingToTarget}
              onDragStart={handleTimelineDragStart}
              onDragEnd={handleTimelineDragEnd}
              playButton={
                <button 
                  className={`${styles.playPauseButton} ${!playersReady ? styles.disabled : ''}`}
                  onClick={handlePlayPause}
                  disabled={!playersReady}
                  title={!playersReady ? 'Waiting for videos to be ready' : ''}
                >
                  <i className={`bi ${isPlaying ? 'bi-pause-fill' : 'bi-play-fill'}`}></i>
                </button>
              }
              globalEndTime={timelineGlobalEndTime}
              rangeStartSeconds={selectedRoundRange ? selectedRoundRange.startSec : null}
              rangeEndSeconds={selectedRoundRange ? selectedRoundRange.endSec : null}
              timerMode={selectedRoundRange ? 'countdown100' : 'normal'}
            />
            <div style={{ transform: 'translateY(-3px)' }}>
              <AudioVolume volume={volume} onVolumeChange={handleVolumeChange} />
            </div>
          </div>
        </div>
      </div>

      {showRestartModal && (
        <RestartScrimProcessing
          isOpen={true}
          onClose={() => setShowRestartModal(false)}
          onRestart={handleRestartProcessing}
          isRestarting={isRestartingProcessing}
        />
      )}

      {showAddTracksModal && scrim_id && (
        <AddScrimTracks
          isOpen={showAddTracksModal}
          onClose={() => setShowAddTracksModal(false)}
          onSuccess={() => {
            setShowAddTracksModal(false);
            loadScrimTracks();
          }}
          existingYoutubeIds={existingYoutubeIds}
          existingStreamStartTimes={existingStreamStartTimes}
          scrimId={scrim_id}
        />
      )}

      {showSettingsModal && (
        <ScrimSettings
          isOpen={showSettingsModal}
          onClose={() => setShowSettingsModal(false)}
          liveEdgeSafetyBuffer={liveEdgeSafetyBuffer}
          onSave={handleSettingsSave}
        />
      )}
    </div>
  );
};

export default Scrim; 